// src/components/Auth/Login.tsx
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm, SubmitHandler } from 'react-hook-form';
import {
  TextField,
  Button,
  Container,
  CssBaseline,
  Typography,
  Box,
  Alert,
  IconButton,
  InputAdornment
} from '@mui/material';
import { login, LoginResponse, requestOtp } from '../../services/authService';
import { BarLoader } from 'react-spinners';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { NotificationManager } from 'react-notifications';
import OTPModal from '../otpModal';
import { useAuth } from '../../context/AuthContext';
interface LoginForm {
  email: string;
  password: string;
  otp?: number;
}

interface LoginProps {
  onLogin: () => void;
}

const Login: React.FC<LoginProps> = ({ onLogin }) => {
  const { login: setAuthUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showOTPModal, setShowOTPModal] = useState(false);

  const [otpState, setOtpState] = useState({ active: false, timer: 0 }); // 🚀 Combined OTP state

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors }
  } = useForm<LoginForm>();

  const [showPassword, setShowPassword] = useState(false);
  const [loginData, setLoginData] = useState<LoginForm>({
    email: '',
    password: ''
  });

  // ⏱️ Timer effect
  useEffect(() => {
    let interval: NodeJS.Timeout;

    if (otpState.active && otpState.timer > 0) {
      interval = setInterval(() => {
        setOtpState((prev) => {
          const newTimer = prev.timer - 1;
          return { ...prev, timer: newTimer };
        });
      }, 1000);
    } else if (otpState.timer === 0 && otpState.active) {
      setOtpState({ active: false, timer: 0 }); // 🚀 OTP expired
    }
    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [otpState.active, otpState.timer]);

  // Effect to handle OTP activation

  const loginFunction = async (data: LoginForm) => {
    try {
      setLoading(true);
      const response: LoginResponse = await login(
        data.email,
        data.password,
        +data.otp
      );
      NotificationManager.success(`Login Successful`, 'Success', 3000);
      setAuthUser(response.data);
      onLogin();
      navigate('/clients');
    } catch (error: any) {
      NotificationManager.error(`Invalid OTP!`, 'Error', 3000);
    } finally {
      setLoading(false);
    }
  };

  const onSubmit: SubmitHandler<LoginForm> = async (data) => {
    if (otpState.active) {
      setShowOTPModal(true); // 🚀 if already active, just reopen modal
      return;
    }

    try {
      setLoading(true);
      setLoginData(data);
      await requestOtp(data.email, data.password);

      // Use single state update to ensure both values are set together
      setOtpState({ active: true, timer: 120 });
      setShowOTPModal(true);
    } catch (error: any) {
      setError('password', { message: 'Invalid email or password' });
    } finally {
      setLoading(false);
    }
  };

  const handleLoginClick = () => {
    if (otpState.active) {
      setShowOTPModal(true);
    }
  };

  const handleResendOtp = async () => {
    try {
      await requestOtp(loginData.email, loginData.password);
      setOtpState({ active: true, timer: 120 });
    } catch (error) {
      // Handle error silently
    }
  };

  const handleTogglePasswordVisibility = () => {
    setShowPassword((prevShowPassword) => !prevShowPassword);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        width: '100%',
        height: '100vh'
      }}
    >
      <Container
        component="main"
        maxWidth="xs"
        style={{
          textAlign: 'center',
          border: '1px solid #ddd',
          boxShadow: '0 0 10px rgba(0, 0, 0, 0.1)',
          padding: '20px',
          borderRadius: '8px',
          marginLeft: '15px',
          marginRight: '15px'
        }}
      >
        <CssBaseline />
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center'
          }}
        >
          <Box sx={{ mt: 2, mb: 3 }} onClick={() => navigate('/')}>
            <span>
              <span>
                <strong
                  style={{
                    color: '#1565C0',
                    fontSize: '30px',
                    fontFamily: 'Roboto'
                  }}
                >
                  AK
                </strong>{' '}
                <br />
              </span>
              FinTax Advisors
            </span>
          </Box>

          <Box
            component="form"
            onSubmit={handleSubmit(onSubmit)}
            noValidate
            sx={{ mt: 1 }}
          >
            <TextField
              label="Email"
              type="email"
              {...register('email', { required: 'Email is required' })}
              fullWidth
              margin="normal"
              error={!!errors.email}
              helperText={errors.email?.message}
            />
            <TextField
              label="Password"
              type={showPassword ? 'text' : 'password'}
              {...register('password', { required: 'Password is required' })}
              fullWidth
              margin="normal"
              error={!!errors.password}
              helperText={errors.password?.message}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleTogglePasswordVisibility}
                      edge="end"
                    >
                      {showPassword ? <Visibility /> : <VisibilityOff />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
              sx={{
                '& input:-webkit-autofill': {
                  transitionDelay: '9999s'
                },
                '& input:-webkit-autofill:focus': {
                  transitionDelay: '9999s'
                },
                '& input:-webkit-autofill:hover': {
                  transitionDelay: '9999s'
                },
                '& input:-webkit-autofill:active': {
                  transitionDelay: '9999s'
                },
                '& input:-internal-autofill-selected': {
                  backgroundColor: 'white !important'
                }
              }}
            />
            <Button
              type={otpState.active ? 'button' : 'submit'}
              variant="contained"
              color="primary"
              fullWidth
              disabled={loading}
              sx={{ 
                mt: 3, 
                height: 40,
                '&.Mui-disabled': {
                  backgroundColor: 'primary.main',
                  color: 'white',
                  opacity: 0.7
                }
              }}
              onClick={otpState.active ? handleLoginClick : undefined}
            >
              {(() => {
                if (loading) {
                  return (
                    <BarLoader
                      color="#ffffff"
                      loading={loading}
                      height={4}
                      width={150}
                    />
                  );
                } else if (otpState.active) {
                  return `Enter OTP - ${otpState.timer}s remaining`;
                } else {  
                  return 'Log in';
                }
              })()}
            </Button>
          </Box>
          {errors.password && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {errors.password.message}
            </Alert>
          )}
        </Box>
      </Container>
      <OTPModal
        open={showOTPModal}
        onClose={() => setShowOTPModal(false)}
        onSubmit={(otp) => loginFunction({ ...loginData, otp: +otp })}
        onResend={handleResendOtp}
        otpActive={otpState.active}
        otpTimer={otpState.timer}
      />
    </Box>
  );
};

export default Login;
